import { useCallback } from 'react';
import { useI18n } from '../i18n';
import { AssetListView } from './AssetListView';
import styles from './BookableAssetSearchResults.module.css';

type BookableAssetSearchResultsProps = {
  records: Record<string, unknown>[];
  total: number;
  loading: boolean;
  loadingMore: boolean;
  catalogExpanding?: boolean;
  hasMore: boolean;
  searchMode: boolean;
  onSelect: (asset: Record<string, unknown>) => void;
  onLoadMore: () => void;
  showBook?: boolean;
  onBook?: (asset: Record<string, unknown>) => void;
  bookDisabled?: boolean;
  bookDisabledReason?: string;
  theme?: 'dark' | 'light';
  emptyMessage?: string;
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
  /** When true, omit the inline “Showing X of Y” (e.g. shown beside filters on PC browse). */
  hideCount?: boolean;
};

export function BookableAssetSearchResults({
  records,
  total,
  loading,
  loadingMore,
  catalogExpanding = false,
  hasMore,
  searchMode,
  onSelect,
  onLoadMore,
  showBook = false,
  onBook,
  bookDisabled = false,
  bookDisabledReason,
  theme = 'light',
  emptyMessage,
  emptyActionLabel,
  onEmptyAction,
  hideCount = false,
}: BookableAssetSearchResultsProps) {
  const { t } = useI18n();

  const onLoadMoreClick = useCallback(() => {
    onLoadMore();
  }, [onLoadMore]);

  if (loading && records.length === 0) {
    return (
      <p className={styles.status} role="status">
        {t('assets.search.loading')}
      </p>
    );
  }

  if (records.length === 0 && catalogExpanding) {
    return (
      <p className={styles.status} role="status">
        {t('assets.search.loadingMoreListings')}
      </p>
    );
  }

  if (!loading && records.length === 0) {
    return (
      <div className={styles.emptyWrap} role="status">
        <p className={styles.status}>{emptyMessage ?? t('assets.filter.noMatch')}</p>
        {emptyActionLabel && onEmptyAction ? (
          <button type="button" className={styles.loadMoreButton} onClick={onEmptyAction}>
            {emptyActionLabel}
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {!hideCount && (total > 0 || catalogExpanding) ? (
        <p className={styles.count} role="status">
          {catalogExpanding
            ? t('assets.search.resultCountPartial', { count: records.length })
            : t('assets.search.resultCount', { count: records.length, total })}
          {catalogExpanding ? (
            <span className={styles.loadingMoreHint}> {t('assets.search.loadingMoreListings')}</span>
          ) : null}
        </p>
      ) : null}
      <AssetListView
        assets={records}
        onSelect={onSelect}
        theme={theme}
        layout="responsiveGrid"
        showBook={showBook}
        onBook={onBook}
        bookDisabled={bookDisabled}
        bookDisabledReason={bookDisabledReason}
        showFavoriteHeart
      />
      {!searchMode && hasMore ? (
        <div className={styles.loadMoreWrap}>
          <button
            type="button"
            className={styles.loadMoreButton}
            onClick={onLoadMoreClick}
            disabled={loadingMore}
          >
            {loadingMore ? t('assets.search.loadingMore') : t('assets.search.loadMore')}
          </button>
        </div>
      ) : null}
      {!searchMode && !hasMore && !catalogExpanding && records.length > 0 ? (
        <p className={styles.endOfList} role="status">
          {t('assets.search.endOfList')}
        </p>
      ) : null}
    </div>
  );
}
